import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import { ToastProvider } from './contexts/ToastContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import AppShell from './components/AppShell';
import { getPostAuthPath } from './utils/postAuthRoute';
import './App.css';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const AIAssistantPage = lazy(() => import('./pages/AIAssistantPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const DemoPage = lazy(() => import('./pages/DemoPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const SharedAnalysisPage = lazy(() => import('./pages/SharedAnalysisPage'));
const SharedExceptionPage = lazy(() => import('./pages/SharedExceptionPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const DocsPage = lazy(() => import('./pages/DocsPage'));
const ExceptionDetailPage = lazy(() => import('./pages/ExceptionDetailPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const GraphPage = lazy(() => import('./pages/GraphPage'));
const UptimePage = lazy(() => import('./pages/UptimePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ProductPage = lazy(() => import('./pages/ProductPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));

function LoadingScreen() {
  return (
    <div className="loading-screen" role="status" aria-live="polite" aria-busy="true">
      <img
        src="/logo-mark.svg"
        className="loading-icon"
        alt=""
        aria-hidden="true"
        width={32}
        height={32}
      />
      <span>Loading...</span>
    </div>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const loggedInMarketingInShell = user && ['/pricing', '/contact'].includes(location.pathname);
  const isAppRoute =
    [
      '/dashboard',
      '/settings',
      '/agent',
      '/admin',
      '/onboarding',
      '/search',
      '/graph',
      '/reports',
      '/uptime',
    ].includes(location.pathname) ||
    location.pathname.startsWith('/exceptions/') ||
    loggedInMarketingInShell;
  const isSharedRoute = location.pathname.startsWith('/shared/');
  const isDocsAsApp = user && location.pathname === '/docs';
  const showFooter = !isAppRoute && !isSharedRoute && !isDocsAsApp && location.pathname !== '/';
  const hasSidebarLayout =
    user &&
    ([
      '/dashboard',
      '/graph',
      '/settings',
      '/agent',
      '/admin',
      '/onboarding',
      '/search',
      '/reports',
      '/uptime',
      '/docs',
      '/pricing',
      '/contact',
    ].includes(location.pathname) ||
      location.pathname.startsWith('/exceptions/'));

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className={`app${hasSidebarLayout ? ' app-with-sidebar' : ''}`}>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      {!isAppRoute && !isSharedRoute && <Navbar />}
      <main
        id="main-content"
        className={isAppRoute ? 'main-content main-content-full' : 'main-content'}
        tabIndex={-1}
      >
        <ErrorBoundary>
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/demo" element={<DemoPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/product" element={<ProductPage />} />
              <Route
                path="/contact"
                element={
                  user ? (
                    <AppShell>
                      <ContactPage />
                    </AppShell>
                  ) : (
                    <ContactPage />
                  )
                }
              />
              <Route
                path="/platform"
                element={
                  user ? <Navigate to={getPostAuthPath()} replace /> : <Navigate to="/" replace />
                }
              />
              <Route
                path="/features"
                element={
                  user ? <Navigate to={getPostAuthPath()} replace /> : <Navigate to="/" replace />
                }
              />
              <Route
                path="/pricing"
                element={
                  user ? (
                    <AppShell>
                      <PricingPage />
                    </AppShell>
                  ) : (
                    <PricingPage />
                  )
                }
              />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route
                path="/login"
                element={user ? <Navigate to={getPostAuthPath()} /> : <LoginPage />}
              />
              <Route
                path="/register"
                element={user ? <Navigate to="/onboarding" /> : <RegisterPage />}
              />
              <Route
                path="/reset-password"
                element={user ? <Navigate to="/dashboard" /> : <ResetPasswordPage />}
              />
              <Route
                path="/onboarding"
                element={user ? <OnboardingPage /> : <Navigate to="/login" />}
              />
              <Route
                path="/dashboard"
                element={
                  user ? (
                    <AppShell>
                      <Dashboard />
                    </AppShell>
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route
                path="/graph"
                element={
                  user ? (
                    <AppShell>
                      <GraphPage />
                    </AppShell>
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route
                path="/agent"
                element={
                  user ? (
                    <AppShell>
                      <AIAssistantPage />
                    </AppShell>
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route path="/ai-assistant" element={<Navigate to="/agent" replace />} />
              <Route path="/ai-analysis" element={<Navigate to="/agent" replace />} />
              <Route
                path="/settings"
                element={
                  user ? (
                    <AppShell>
                      <SettingsPage />
                    </AppShell>
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route
                path="/admin"
                element={
                  user?.role === 'admin' ? (
                    <AppShell>
                      <AdminPage />
                    </AppShell>
                  ) : (
                    <Navigate to="/dashboard" />
                  )
                }
              />
              <Route
                path="/docs"
                element={
                  user ? (
                    <AppShell>
                      <DocsPage />
                    </AppShell>
                  ) : (
                    <>
                      <DocsPage publicLayout />
                    </>
                  )
                }
              />
              <Route
                path="/search"
                element={
                  user ? (
                    <AppShell>
                      <SearchPage />
                    </AppShell>
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route
                path="/reports"
                element={
                  user ? (
                    <AppShell>
                      <ReportsPage />
                    </AppShell>
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route
                path="/uptime"
                element={
                  user ? (
                    <AppShell>
                      <UptimePage />
                    </AppShell>
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route
                path="/exceptions/:id"
                element={
                  user ? (
                    <AppShell>
                      <ExceptionDetailPage />
                    </AppShell>
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route path="/shared/exception/:token" element={<SharedExceptionPage />} />
              <Route path="/shared/:token" element={<SharedAnalysisPage />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>
      {showFooter && <Footer />}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <FeatureFlagsProvider>
          <ToastProvider>
            <AppRoutes />
          </ToastProvider>
        </FeatureFlagsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
